import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { CanvasGroup } from "../components/canvas";
import MultipleSpinners from "../components/multiple-spinners";
import PostModal from "../components/post-modal";
import Toolbar from "../components/toolbar";
import { createPost, docIdFromDrawingAndUser, fetchColoringActions, fetchDrawingById, saveColoringActions, uploadImageDataUrl } from "../firebase";
import MagicWand from "../magic-wand";
import { removeColoringActions, storeColoringActions } from "../storage";
import { setMessage, setShouldAskToSignIn } from "../store";
import '../styles/mobile-coloring-screen.scss';
import { hexToRgb, isMobileOrTablet } from "../utils";
import { isSignedIn } from "../auth";

const colorThreshold = 5
const minImgDimension = 1000

const COLORING_EVENTS = {
  MOUSE_DOWN: 'MOUSE_DOWN',
  MOUSE_UP: 'MOUSE_UP',
  MOUSE_MOVE: 'MOUSE_MOVE',
  TOUCH_MOVE: 'TOUCH_MOVE',
  TOUCH_START: 'TOUCH_START',
  TOUCH_END: 'TOUCH_END',
}

function MobileColoringScreen() {
  const dispatch = useDispatch()
  const params = useParams();
  const drawingId = params.drawingId
  const magicWandRef = useRef(null)
  const bigImageInfo = useRef({})
  const bigImageSize = useRef({})
  const crtUser = useSelector(state => state.crtUser)
  const [loading, setLoading] = useState(true)
  const [imgLoaded, setImageLoaded] = useState(false)
  const [drawing, setDrawing] = useState({})
  const [selectedColor, setSelectedColor] = useState({ color: '#ff0000', type: 'solid' })
  const [actions, setActions] = useState([])
  const [crtAction, setCrtAction] = useState(null)
  const [brushPoints, setBrushPoints] = useState([])
  const [tools, setTools] = useState({ Bucket: true })
  const [stepIdx, setStepIdx] = useState(0)
  const [isDrawing, setIsDrawing] = useState(false)
  const [brushSize, setBrushSize] = useState(10)
  const [scale, setScale] = useState(1)
  const [pickPoint, setPickPoint] = useState({ x: null, y: null })
  const [panEnabled, setPanEnabled] = useState(false)
  const [panStart, setPanStart] = useState(null)
  const [panTranslate, setPanTranslate] = useState({ x: 0, y: 0 })
  const [postText, setPostText] = useState('')
  const [isPostModalOpen, setIsPostModalOpen] = useState(false)
  const [bigSvgUrl, setBigSvgUrl] = useState(null)
  const [imageDataUrl, setImgDataUrl] = useState(null)
  const [imgDataUrlRequest, setImgDataUrlRequest] = useState({ requesting: false, forAction: null })

  useEffect(() => {
    magicWandRef.current = new MagicWand()
  }, [])

  useEffect(() => {
    fetchDrawingById(drawingId).then(_drawing => {
      setDrawing(_drawing)
      setLoading(false)
    })
  }, [drawingId])

  useEffect(() => {
    if (drawingId && crtUser.uid && imgLoaded) {
      fetchColoringActions(drawingId, crtUser.uid)
        .then(res => {
          if (res && res.actions.length) {
            setActions(injectMaskData(res.actions))
            setStepIdx(res.actions.length)
          }
        })
    }
  }, [drawingId, crtUser, imgLoaded])

  useEffect(() => {
    document.addEventListener('keydown', handkeKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handkeKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [scale])

  useEffect(() => {
    if (actions.length) {
      storeColoringActions(drawingId, prepareActionsToSave())
    }
  }, [actions])

  async function handleSave() {
    if (isSignedIn()) {
      setImgDataUrlRequest({ requesting: true, forAction: 'save' })
    } else {
      dispatch(setShouldAskToSignIn({ action: 'save' }))
    }
  }

  function handlePost() {
    if (isSignedIn()) {
      setImgDataUrlRequest({ requesting: true, forAction: 'openPostModal' })
    } else {
      dispatch(setShouldAskToSignIn({ action: 'post' }))
    }
  }

  function handkeKeyDown(evt) {
    setPanEnabled((evt.code === 'Space') && (scale > 1))
  }

  function handleKeyUp(evt) {
    if (evt.code === 'Space') setPanEnabled(false)
  }

  function prepareActionsToSave() {
    return actions.map(action => ({ ...action, mask: null }))
  }

  function injectMaskData(actions) {
    return actions.map(action => {
      const { width, height } = bigImageInfo.current.imageData
      const x = parseInt(width * action.xR)
      const y = parseInt(height * action.yR)

      const _action = {
        ...action,
        x,
        y,
        mask: calculateMask(bigImageInfo.current.imageData, { x, y })
      }

      if (action.points) {
        return {
          ..._action,
          points: action.points.map(({ xR, yR }) => ({
            x: parseInt(width * xR),
            y: parseInt(height * yR),
            xR,
            yR
          }))
        }
      }

      return _action
    })
  }

  function buildImageInfo(evt) {
    const img = evt.target
    const width = img.width
    const height = img.height

    if (width < height) {
      const ratio = minImgDimension / width
      bigImageSize.current = {
        width: minImgDimension,
        height: height * ratio,
        ratio
      }
    } else {
      const ratio = minImgDimension / height
      bigImageSize.current = {
        height: minImgDimension,
        width: width * ratio,
        ratio
      }
    }

    setBigSvgUrl(drawing.svgUrl)
  }

  function buildBigImageInfo(evt) {
    const img = evt.target
    const width = img.width
    const height = img.height
    const tempCanvas = document.createElement("canvas").getContext("2d")

    img.crossOrigin = "Anonymous"

    tempCanvas.canvas.width = width
    tempCanvas.canvas.height = height
    tempCanvas.drawImage(img, 0, 0, width, height)

    bigImageInfo.current = {
      imageData: tempCanvas.getImageData(0, 0, width, height),
      boundingBox: img.getBoundingClientRect()
    }

    // Workaround: Wait a bit until the image loads
    setTimeout(() => setImageLoaded(true), 500)
  }

  function calculateMask(imageData, { x, y }) {
    const image = {
      data: imageData.data,
      width: imageData.width,
      height: imageData.height,
      bytes: 4
    }

    return magicWandRef.current.floodFill(image, x, y, colorThreshold, null, true)
  }

  function handleMouseDown(evt) {
    if(isMobileOrTablet()) return;

    const x = parseInt(evt.nativeEvent.offsetX * bigImageSize.current.ratio)
    const y = parseInt(evt.nativeEvent.offsetY * bigImageSize.current.ratio)

    const xR = x / bigImageInfo.current.imageData.width
    const yR = y / bigImageInfo.current.imageData.height

    if (panEnabled) {
      const x = evt.nativeEvent.clientX - panTranslate.x
      const y = evt.nativeEvent.clientY - panTranslate.y
      setPanStart({ x, y })
      return
    }

    handleColoringEvent({ x, y, xR, yR, type: COLORING_EVENTS.MOUSE_DOWN })
  }

  function calcTouchCoordinates(evt) {
    const touch = evt.nativeEvent.targetTouches[0]

    if (touch) {
      const target = evt.target.getBoundingClientRect()
      const x = parseInt((touch.pageX - target.left) * bigImageSize.current.ratio)
      const y = parseInt((touch.pageY - target.top) * bigImageSize.current.ratio)

      const xR = x / bigImageInfo.current.imageData.width
      const yR = y / bigImageInfo.current.imageData.height

      return { x, y, xR, yR }
    }
  }

  function handleTouchStart(evt) {
    const { x, y, xR, yR } = calcTouchCoordinates(evt)
    handleColoringEvent({ x, y, xR, yR, type: COLORING_EVENTS.TOUCH_START })
  }

  function handleMouseMove(evt) {
    const x = parseInt(evt.nativeEvent.offsetX * bigImageSize.current.ratio)
    const y = parseInt(evt.nativeEvent.offsetY * bigImageSize.current.ratio)

    const xR = x / bigImageInfo.current.imageData.width
    const yR = y / bigImageInfo.current.imageData.height

    if (panEnabled && panStart) {
      const x = evt.nativeEvent.clientX
      const y = evt.nativeEvent.clientY

      const imgW = bigImageInfo.current.imageData.width
      const imgH = bigImageInfo.current.imageData.height

      let deltaX = x - panStart.x
      if (deltaX < 0) deltaX = Math.max(deltaX, (1 - scale) * imgW / 2)
      if (deltaX > 0) deltaX = Math.min(deltaX, (scale - 1) * imgW / 2)

      let deltaY = y - panStart.y
      if (deltaY < 0) deltaY = Math.max(deltaY, (1 - scale) * imgH / 2)
      if (deltaY > 0) deltaY = Math.min(deltaY, (scale - 1) * imgH / 2)

      setPanTranslate({ x: deltaX, y: deltaY })
      return
    }

    handleColoringEvent({ x, y, xR, yR, type: COLORING_EVENTS.MOUSE_MOVE })
  }

  function handleTouchMove(evt) {
    const { x, y, xR, yR } = calcTouchCoordinates(evt)
    handleColoringEvent({ x, y, xR, yR, type: COLORING_EVENTS.TOUCH_MOVE })
  }

  function handleMouseUp() {
    handleColoringEvent({ type: COLORING_EVENTS.MOUSE_UP })
  }

  function handleTouchEnd() {
    handleColoringEvent({ type: COLORING_EVENTS.TOUCH_END })
  }

  function handleColoringEvent({ x, y, xR, yR, type }) {
    if (type === COLORING_EVENTS.MOUSE_DOWN || type === COLORING_EVENTS.TOUCH_START) {
      if (tools.Brush || tools.Eraser || tools.Pencil || tools.Spray) {
        setIsDrawing(true)
        setBrushPoints([{ x, y, xR, yR }])
      }

      if (tools.Bucket || tools.Scissor) {
        handleBucketAction({ x, y, xR, yR })
      }

      if (tools.Picker) {
        setPickPoint({ x, y })
      }
    }

    if (type === COLORING_EVENTS.MOUSE_MOVE || type === COLORING_EVENTS.TOUCH_MOVE) {
      if ((tools.Brush || tools.Eraser || tools.Pencil || tools.Spray) && isDrawing) {
        setBrushPoints([...brushPoints, { x, y, xR, yR }])
        handleDrawingAction(true)
      }
    }

    if (type === COLORING_EVENTS.MOUSE_UP || type === COLORING_EVENTS.TOUCH_END) {
      setPanStart(null)

      if (tools.Brush || tools.Eraser || tools.Pencil || tools.Spray) {
        setIsDrawing(false)
        handleDrawingAction(false)
      }
    }
  }

  function handleBucketAction({ x, y, xR, yR }) {
    const mask = calculateMask(bigImageInfo.current.imageData, { x, y })

    // do nothing if the mask is empty
    if (!mask) return

    const colorArr = tools.Scissor ? hexToRgb('#ffffff') : // Always white for Scissor
      selectedColor.type === 'solid' ? hexToRgb(selectedColor.color) : []

    const action = {
      x,
      xR,
      y,
      yR,
      isSolidColor: tools.Scissor || selectedColor.type === 'solid', // Always solid for Scissor
      colorArr,
      colorUrl: selectedColor.type !== 'solid' ? selectedColor.color : null,
      mask,

    }

    const _actions = [...actions.slice(0, stepIdx), action]

    setStepIdx(_actions.length)
    setActions(_actions)
  }

  function handleDrawingAction(isDrawing) {
    const action = {
      usingBrush: tools.Brush || tools.Eraser || tools.Pencil || tools.Spray,
      isSpray: tools.Spray,
      points: brushPoints,
      isSolidColor: selectedColor.type === 'solid',
      colorHex: tools.Eraser ? '#fff' : selectedColor.color,
      lineWidth: tools.Pencil ? 1 : (brushSize || 20),
      colorUrl: selectedColor.type !== 'solid' ? selectedColor.color : null,
    }

    if (isDrawing) {
      setCrtAction(action)
    } else {
      setCrtAction(null)
      setActions([...actions.slice(0, stepIdx), action])
      setStepIdx(stepIdx + 1)
    }

  }

  function handleToolSelect(tool, size) {
    const toolsList = ['Picker', 'Brush', 'Pencil', 'Eraser', 'Bucket', 'Scissor', 'Spray', 'Trash']

    const toolsState = toolsList.reduce((acc, crt) => {
      acc[crt] = (crt === tool)

      return acc
    }, {})

    if (toolsState.Trash) {
      cleanColoringActions()
    }

    setTools(toolsState)
    setBrushSize(size)
  }

  function cleanColoringActions() {
    setActions([])
    removeColoringActions(drawingId)
  }

  function handleColorPick(colorHex) {
    setSelectedColor({ color: colorHex, type: 'solid' })
  }

  function handleColorSelect(colorAndType) {
    setSelectedColor(colorAndType)
  }

  function mergedActions() {
    if (crtAction) {
      return [
        ...actions.slice(0, stepIdx),
        crtAction
      ]
    }

    return actions.slice(0, stepIdx)
  }

  function zoomIn() {
    setScale(Math.min(5, scale * 1.1))
  }

  function zoomOut() {
    const _scale = Math.max(scale * 0.9, 1)

    const imgW = bigImageInfo.current.imageData.width
    const imgH = bigImageInfo.current.imageData.height

    let transX = panTranslate.x
    let transY = panTranslate.y

    if (_scale < 1) {
      transX = 0
      transY = 0
    } else {
      if (transX < 0) transX = Math.max(transX, (1 - _scale) * imgW / 2)
      if (transX > 0) transX = Math.min(transX, (_scale - 1) * imgW / 2)

      if (transY < 0) transY = Math.max(transY, (1 - _scale) * imgH / 2)
      if (transX > 0) transY = Math.min(transY, (_scale - 1) * imgH / 2)
    }

    setPanTranslate({ x: transX, y: transY })
    setScale(_scale)
  }

  function restZoom() {
    setScale(1)
    setPanTranslate({ x: 0, y: 0 })
  }

  function handleZoomChange(action) {
    switch (action) {
      case 'in':
        zoomIn()
        break
      case 'out':
        zoomOut()
        break
      case 'fs': restZoom()
    }
  }

  function handleImgUrlReadyChange(_imgDataUrl) {
    setImgDataUrl(_imgDataUrl)

    if (imgDataUrlRequest.forAction === 'save') {
      const docId = docIdFromDrawingAndUser(drawingId, crtUser.uid)

      uploadImageDataUrl(docId, _imgDataUrl).then(imageUrl => {
        return saveColoringActions(drawingId, crtUser.uid, prepareActionsToSave(), imageUrl)
          .then(() => {
            dispatch(setMessage({
              text: 'The changes have been saved successfully',
              status: 'success'
            }))
          })
      })
        .catch(() => {
          dispatch(setMessage({
            text: 'Something went wrong while saving your changes. Please try again!',
            status: 'error'
          }))
        })
    }

    if (imgDataUrlRequest.forAction === 'openPostModal') {
      setIsPostModalOpen(true)
    }

    setImgDataUrlRequest({ requesting: false, forAction: null })
  }

  function handlePostTextChange(evt) {
    setPostText(evt.target.value)
  }

  function handleConfirmPost() {
    setIsPostModalOpen(false)

    const docId = uuidv4()

    uploadImageDataUrl(docId, imageDataUrl).then(imageUrl => {
      return createPost(drawingId, crtUser.uid, imageUrl, postText)
        .then(() => {
          dispatch(setMessage({ text: 'Your post has been published successfully!', status: 'success' }))
          setPostText('')
          setIsPostModalOpen(false)
        })
    })
      .catch(err => {
        console.error(err)
        dispatch(setMessage({ text: 'Something went wrong while creating your post. Please try again!', status: 'error' }))
      })
  }

  function handleCancelPost() {
    setPostText('')
    setIsPostModalOpen(false)
  }

  return (
    <div id="mobile-coloring-screen">
      <div id="wrapper">
        {loading && <MultipleSpinners />}

        {!loading &&
          <>
            {/* <Toolbar
              tools={tools}
              onToolSelect={handleToolSelect}
              onZoomChange={handleZoomChange}
              onRedo={() => setStepIdx(stepIdx + 1)}
              onUndo={() => setStepIdx(stepIdx - 1)}
              undoDisabled={stepIdx === 0}
              redoDisabled={stepIdx === actions.length}
              onColorPick={handleColorSelect}
              pickedColor={selectedColor}
              onSave={handleSave}
              onPost={handlePost}
              isReadyToSaveOrShare={actions.length}
              drawingName={drawing.name}
              imageDataUrl={imageDataUrl}
            /> */}

            <div id="coloring-area" className={(tools['Picker'] ? 'color-picker' : '')} >
              <img
                alt="vector"
                id="raw-picture"
                src={drawing.svgUrl}
                className={panEnabled ? 'cursor-grab' : ' '}

                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}

                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}

                onLoad={buildImageInfo}
                draggable={false}
                style={{ transform: `translate(${panTranslate.x}px, ${panTranslate.y}px) scale(${scale})` }}
              />

              {bigSvgUrl && <img
                id="raw-big-picture"
                src={bigSvgUrl}
                onLoad={buildBigImageInfo}
                width={bigImageSize.current.width}
                height={bigImageSize.current.height}
              />}

              <CanvasGroup
                actions={mergedActions()}
                boundingBox={bigImageInfo.current.boundingBox}
                pickPoint={pickPoint}
                onColorPick={handleColorPick}
                scale={scale}
                scaleUpRatio={bigImageSize.current.ratio}
                translate={panTranslate}
                onImgDataUrlReady={handleImgUrlReadyChange}
                imageData={bigImageInfo.current.imageData}
                requestingImgDataUrl={imgDataUrlRequest.requesting}
              />
            </div>

            <PostModal
              isOpen={isPostModalOpen}
              imageUrl={imageDataUrl}
              text={postText}
              isNewPost={true}
              onTextChange={handlePostTextChange}
              onConfirm={handleConfirmPost}
              onCancel={handleCancelPost}
              onToggle={() => setIsPostModalOpen(!isPostModalOpen)}
            />
          </>
        }
      </div>
    </div>
  );
}

export default MobileColoringScreen;
